import { computed, ref, watch } from 'vue';
import useAvailabilityBigTicketStore from '~/stores/useAvailabilityBigTicketStore';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import { injectLocal, provideLocal } from '@vueuse/core';
import { useBrand } from '~/composables/useBrand';
import useMembersOfProduct from '~/composables/useMembersOfProduct';
import { getStoreLocation } from '~/modules/availability/utils/localStorage';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useUpc from '~/modules/upc/composables/useUpc';
import { getBigTicketZipCookie } from '~/utils/cookiesUtils';

import { availabilityBigTicketParser } from '../utils/availabilityBigTicketParser';
import { getOnOrderAvailability } from '~/modules/availability/utils/orderEligibility';
import { useBigTicketZipCode } from './useBigTicketZipCode';

/**
 * @type {import('vue').InjectionKey<import('./useAvailabilityBigTicket').AvailabilityReturnData>}
 */
const AVAILABILITY_BT_REGISTRY = Symbol.for('AVAILABILITY_BT_REGISTRY');

export const useAvailabilityBigTicket = () => injectLocal(AVAILABILITY_BT_REGISTRY);

export const useAvailabilityBigTicketProvide = () => {
  const { isBcom } = useBrand();
  const { isMemberOfMasterFlag, isBigTicketItemFlag } = useProductFlags();
  const { isBigTicket, isBigTicketV2C } = useFeatureEligibility();
  const { memberProductIdsString } = useMembersOfProduct();
  const { productId } = useProduct();

  const lookupIds = computed(() => memberProductIdsString.value || productId);

  const { selectedUpcId, hasSelectedUpcId } = useUpc();
  const availabilityBigTicketStore = useAvailabilityBigTicketStore();
  const slideoutStore = useSlideoutStore();
  // Todos -  decouple this from useAvailabilityBigTicketProvide composable later
  const { zipCode, setZipCode } = useBigTicketZipCode();

  /** @type {import('vue').Ref<{ [key: number]: import('./useAvailabilityBigTicket').BigTicketAvailability }>} */
  const availabilityUpcs = ref({});

  const availabilityData = computed(() => availabilityBigTicketStore.data);
  const objectKey = computed(() => `${zipCode.value}_${lookupIds.value}`);
  const currentZipCodeAvailabilityData = computed(() => availabilityData.value[objectKey.value]);

  const selectedUpcData = computed(() => availabilityUpcs.value[selectedUpcId.value]);
  const showUnavailableMessageForCurrentUpc = computed(() => selectedUpcData.value?.showUnavailableMessage ?? !hasSelectedUpcId.value);
  const callCenterMessages = {
    bcom: 'Have a question or need advice? Call one of our furniture and mattress experts at <strong>1-866-593-2540</strong>, 8AM-Midnight EST, 7 days/week.',
    mcom: 'Have a question? Talk with our furniture & mattress experts at <strong>1-800-366-6229</strong>.',
  };
  const callCenterCTAMessage = isBcom ? callCenterMessages.bcom : callCenterMessages.mcom;
  const bcomErrorMessage = 'To check availability, call 1-866-593-2540.';
  const deliverabilityMessageForCurrentUpc = computed(() => {
    if (isBcom && selectedUpcData.value?.deliverability?.status === 'ERROR') {
      return bcomErrorMessage;
    }
    return selectedUpcData.value?.deliverability?.deliverabilityMessage;
  });

  const getCurrentDeliveryMessage = (isBigTicketDeliveryActive) => {
    const { deliverabilityMessage = '', status = '' } = selectedUpcData.value?.deliverability ?? {};
    if (isBigTicketDeliveryActive && !getOnOrderAvailability(deliverabilityMessage, status)) {
      return '';
    }
    return deliverabilityMessageForCurrentUpc.value;
  };
  const bigTicketAvailableForCurrentUpc = computed(() => Boolean(selectedUpcData.value?.bigTicketAvailable));
  const isWhiteGloveDelivery = computed(() => isBcom && !isBigTicketV2C && isBigTicket && !isMemberOfMasterFlag);

  const showNotAvailableComponent = computed(() => !!zipCode.value && showUnavailableMessageForCurrentUpc.value && isBigTicketItemFlag);
  const showNoZipCodeComponent = computed(() => !zipCode.value && isBigTicketItemFlag);
  const showShipToZipCodeComponent = computed(() => !!zipCode.value && !showNotAvailableComponent.value && isBigTicketItemFlag);

  /**
   * @param { string } zipCodeValue
   */

  function updateAvailabilityUpcs(zipCodeValue) {
    availabilityUpcs.value = availabilityBigTicketParser({
      availabilityData: availabilityData.value,
      zipCode: zipCodeValue,
      lookupIds: lookupIds.value,
    });
  }

  async function fetchAvailability() {
    await availabilityBigTicketStore.fetchAvailability({ lookupIds: lookupIds.value, zipCode: zipCode.value });
  }

  const isDataLoaded = ref(false);

  watch(
    zipCode,
    async (newZipCode) => {
      if (!newZipCode) return;
      await fetchAvailability();
      updateAvailabilityUpcs(newZipCode);
      isDataLoaded.value = true;
    },
    { immediate: true },
  );

  const dataLoadNeeded = computed(() => getBigTicketZipCookie() || getStoreLocation().zipcode);

  const zipcodeSlideoutShown = computed({
    get() {
      return availabilityBigTicketStore.zipcodeSlideoutShown;
    },
    set(value) {
      availabilityBigTicketStore.setZipCodeSlideoutShown(value);
    },
  });
  const previouslyFocusedElement = computed({
    get() {
      return availabilityBigTicketStore.previouslyFocusedElement;
    },
    set(value) {
      availabilityBigTicketStore.setPreviouslyFocusedElement(value);
    },
  });

  const activeSlideout = computed({
    get() {
      return availabilityBigTicketStore.activeSlideout;
    },
    set(value) {
      availabilityBigTicketStore.setActiveSlideout(value);
    },
  });

  const setStoreZipCode = (slideoutZipCode) => {
    availabilityBigTicketStore.setZip(slideoutZipCode);
  };

  const toggleBTSlideOut = (id) => {
    slideoutStore.toggle(`zipcode-slideout-${id}`);
  };

  provideLocal(AVAILABILITY_BT_REGISTRY, {
    bigTicketAvailableForCurrentUpc,
    deliverabilityMessageForCurrentUpc,
    showNotAvailableComponent,
    showShipToZipCodeComponent,
    showNoZipCodeComponent,
    zipCode,
    callCenterCTAMessage,
    isWhiteGloveDelivery,
    setZipCode,
    activeSlideout,
    zipcodeSlideoutShown,
    previouslyFocusedElement,
    setStoreZipCode,
    toggleBTSlideOut,
    getCurrentDeliveryMessage,
    dataLoadNeeded,
    isDataLoaded,
    currentZipCodeAvailabilityData,
  });
};
